/**
 * When the install sheet may open by itself (PWA install prompt). Per
 * device, in localStorage (app/lib/storage.ts never throws): only counts and
 * times, nothing about the user or patients.
 *
 * Rules:
 * - Never on desktop, never when Velora is already the installed app, never
 *   after the person installed it from here.
 * - "Not now" (or closing the sheet) snoozes it for 7 days.
 * - After 3 dismissals it still comes back, but only weekly: never more than
 *   once in any 7 days, counted from when it was last shown, so a sheet that
 *   was shown and left open does not reappear on the next sign-in either.
 * - At most once per browser session.
 * The user-menu entry "Install app" opens the sheet any time, ignoring all
 * of this.
 */
import { readJson, readString, writeJson, writeString } from '../lib/storage.js';
import type { InstallPlatform } from './platform.js';

export const STORAGE_KEY = 'velora.pwa.install.v1';
export const SESSION_KEY = 'velora.pwa.install.shown';
export const DAY_MS = 24 * 60 * 60 * 1000;
export const SNOOZE_MS = 7 * DAY_MS;
export const WEEKLY_AFTER_DISMISSALS = 3;

export interface SnoozeState {
  dismissals: number;
  /** Epoch ms; the sheet stays closed before this. */
  snoozedUntil: number;
  /** Epoch ms of the last automatic or manual showing. */
  lastShownAt: number;
  /** Installed from the sheet (Android accepted). */
  installed: boolean;
}

export const EMPTY_STATE: SnoozeState = {
  dismissals: 0,
  snoozedUntil: 0,
  lastShownAt: 0,
  installed: false,
};

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
}

/** Whatever is stored, as a valid state (bad or old data counts as none). */
export function normalize(raw: unknown): SnoozeState {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_STATE };
  const r = raw as Record<string, unknown>;
  return {
    dismissals: Math.floor(num(r['dismissals'])),
    snoozedUntil: num(r['snoozedUntil']),
    lastShownAt: num(r['lastShownAt']),
    installed: r['installed'] === true,
  };
}

export function dismiss(s: SnoozeState, now: number): SnoozeState {
  return { ...s, dismissals: s.dismissals + 1, snoozedUntil: now + SNOOZE_MS };
}

export function markShown(s: SnoozeState, now: number): SnoozeState {
  return { ...s, lastShownAt: now };
}

export function markInstalled(s: SnoozeState): SnoozeState {
  return { ...s, installed: true };
}

export interface PromptContext {
  state: SnoozeState;
  now: number;
  platform: InstallPlatform;
  standalone: boolean;
  /** Already shown automatically in this browser session. */
  shownThisSession: boolean;
}

export type PromptDecision =
  | { show: true }
  | {
      show: false;
      reason: 'not-mobile' | 'standalone' | 'installed' | 'snoozed' | 'weekly' | 'session';
    };

export function shouldAutoPrompt(c: PromptContext): PromptDecision {
  if (c.platform === 'other') return { show: false, reason: 'not-mobile' };
  if (c.standalone) return { show: false, reason: 'standalone' };
  if (c.state.installed) return { show: false, reason: 'installed' };
  if (c.now < c.state.snoozedUntil) return { show: false, reason: 'snoozed' };
  if (
    c.state.dismissals >= WEEKLY_AFTER_DISMISSALS &&
    c.state.lastShownAt > 0 &&
    c.now - c.state.lastShownAt < SNOOZE_MS
  ) {
    return { show: false, reason: 'weekly' };
  }
  if (c.shownThisSession) return { show: false, reason: 'session' };
  return { show: true };
}

export function loadState(): SnoozeState {
  return normalize(readJson<unknown>(STORAGE_KEY, null));
}

export function saveState(s: SnoozeState): void {
  writeJson(STORAGE_KEY, s);
}

export function shownThisSession(): boolean {
  return readString(SESSION_KEY, 'session') === '1';
}

export function rememberShownThisSession(): void {
  writeString(SESSION_KEY, '1', 'session');
}
